import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  useGetAgentById,
  useGetAgents,
  usePublishAgent,
  useUpdateAgentAIConfigurations,
  useUpdateWidget,
} from 'features/chat/hooks/use-agents';
import { useAgentChatModels } from 'features/chat/hooks/use-agent-chat-models';
import {
  getAgentToolIds,
  WidgetType,
  type Agent,
  type CustomRule,
  type GuardrailConfig,
  type GuardrailOutputStrategy,
  type IPublishAgentResponse,
  type IUpdateAgentAIConfigurationPayload,
  type IUpdateAgentWidgetPayload,
  type Widget,
} from 'features/chat/agent-chat-module/types/agent.service.type';
import { useConfig } from 'hooks/use-config';
import { useToast } from 'hooks/use-toast';
import { useCommonStore } from 'state/store/common/useCommonStore';
import { RESOURCE_VISIBILITY, normalizeResourceVisibility } from 'types/visibility';
import { AgentConfigurationCard } from './components/agent-configuration-card';
import { EditAgent } from './components/edit-agent';
import { AgentsConfigPreviewSection } from './components/preview-section/agents-config-preview-section';
import { AgentsRetrievalTesting } from 'features/chat/agent-chat-module/components/agents-retrieval-testing';
import { AgentIntegrationHub } from './components/integrations';
import { AgentConversation } from 'features/chat/agent-chat-module/components/agent-conversation/agent-conversation';
import { HttpError } from 'lib/https';
import { AgentDetailsTopBar } from './components/agent-details-top-bar';
import { MobilePreviewPanel, MobilePreviewToggle } from './components/mobile-preview-sheet';
import {
  parseAllowedDomains,
  parseChatbotWidgetData,
} from './components/integrations/agent-integrations-chatbot/utils';
import {
  canConfigureAgentWidget,
  canEditAgentConfig,
  hasAgentShareAccess,
  isAgentOwnerAccess,
} from './utils/agent-share-access';
import {
  buildAgentConfigPayload,
  capabilitiesFromSectionState,
  getAgentConfigValues,
  resolveInitialBasePrompt,
} from './utils/agent-config-payload';
import {
  AgentCapabilitiesState,
  BASE_PROMPT_SYNC_NOTICE,
  computeDefaultBasePrompt,
  resolveSyncedBasePrompt,
} from 'utils/agent-default-prompt';

/** Plain readable copy when UILM keys are missing or still untranslated. */
const agentText = (
  t: TFunction,
  key: string,
  fallback: string,
  options?: Record<string, unknown>
) => {
  const value = t(key, { defaultValue: fallback, ...options });
  if (!value || value === key || /^[A-Z][A-Z0-9_]+$/.test(String(value))) {
    return fallback;
  }
  return String(value);
};

/** The backend seeds the greeting with a `[NAME]` placeholder; show the agent's name instead. */
const formatGreeting = (greeting: string, agentName: string) =>
  greeting.replace(/\[NAME\]/g, agentName);

const buildPublishWidgetPayload = (agent: Agent, formWidget: Widget): Widget => {
  const parsed = parseChatbotWidgetData(agent.widget);
  const restSettings = (agent.widget?.widget_settings ?? []).filter(
    (item) => item.widget_type !== WidgetType.CHAT
  );
  const chatFeStyle =
    agent.widget?.widget_settings?.find((item) => item.widget_type === WidgetType.CHAT)?.fe_style ??
    JSON.stringify(parsed.designSettings);

  return {
    ...(agent.widget ?? {}),
    ...formWidget,
    name: agent.name,
    logo_url: agent.widget?.logo_url ?? '',
    logo_id: agent.widget?.logo_id ?? null,
    greeting: formatGreeting(
      formWidget.greeting || agent.widget?.greeting || parsed.greeting,
      agent.name
    ),
    site_url: agent.widget?.site_url ?? null,
    allowed_domains: formWidget.allowed_domains ?? agent.widget?.allowed_domains ?? [],
    allow_visitor_tool_connect:
      agent.widget?.allow_visitor_tool_connect ?? agent.widget?.AllowVisitorToolConnect ?? true,
    host_cookie_name: agent.widget?.host_cookie_name ?? agent.widget?.HostCookieName ?? null,
    brand_color: agent.widget?.brand_color ?? null,
    fe_script: agent.widget?.fe_script ?? null,
    show_agent_name: agent.widget?.show_agent_name ?? false,
    predefined_questions: (
      formWidget.predefined_questions ??
      agent.widget?.predefined_questions ??
      []
    ).filter(Boolean),
    enable_predefined_questions:
      formWidget.enable_predefined_questions ?? agent.widget?.enable_predefined_questions ?? false,
    widget_settings: [
      ...restSettings,
      {
        widget_type: WidgetType.CHAT,
        fe_style: chatFeStyle,
      },
    ],
  };
};

const getAgentDetailsTabs = (agentId: string, t: TFunction) => [
  { label: agentText(t, 'EDIT_AGENT', 'Edit Agent'), href: `/agent-details/${agentId}/edit` },
  {
    label: agentText(t, 'CONFIGURATION', 'Configuration'),
    href: `/agent-details/${agentId}/configuration`,
  },
  {
    label: agentText(t, 'CHATBOT_INTGERATION', 'Integration'),
    href: `/agent-details/${agentId}/integrations`,
  },
  {
    label: agentText(t, 'CONVERSATIONS', 'Conversations'),
    href: `/agent-details/${agentId}/conversations`,
  },
];

export default function AgentDetailsPage() {
  const { t } = useTranslation();
  const { agentId = '' } = useParams();
  const { pathname, search, state: locationState } = useLocation();
  const navigate = useNavigate();

  // Redirect base route to /edit
  useEffect(() => {
    if (pathname === `/agent-details/${agentId}`) {
      navigate(`/agent-details/${agentId}/edit`, { replace: true });
    }
  }, [pathname, agentId, navigate]);
  const { configurationSettings } = useConfig();
  const { toast } = useToast();
  const blocksKey = configurationSettings?.blocksKey || '';
  const defaultJsonResponseFormat = useMemo(
    () =>
      JSON.stringify(
        {
          answer: 'string',
          question: 'string',
        },
        null,
        2
      ),
    []
  );
  const [sectionState, setSectionState] = useState<Record<string, boolean>>({
    'knowledge-base': true,
    tools: false,
    skills: false,
    subagents: false,
    memory: false,
    'human-handoff': false,
    guardrails: true,
  });
  const defaultWelcomeMessage = agentText(
    t,
    'DEFAULT_WELCOME_MESSAGE_FINANCE_ASSISTANT',
    "Welcome! My name is Finance Assistant. If you need any assistance or have any questions, I'm here to help."
  );
  const [selectedLlmModel, setSelectedLlmModel] = useState('');
  const [selectedLlmProvider, setSelectedLlmProvider] = useState('');
  const [selectedLlmModelId, setSelectedLlmModelId] = useState('');
  const [isBlocksProvider, setIsBlocksProvider] = useState(true);
  const [responseType, setResponseType] = useState('text');
  const [responseFormat, setResponseFormat] = useState<string | null>(null);
  const [basePrompt, setBasePrompt] = useState('');
  const [temperatureValue, setTemperatureValue] = useState(0.7);
  const [maxResponseValue, setMaxResponseValue] = useState(10000);
  const [isModelParametersOpen, setIsModelParametersOpen] = useState(false);
  const [enableQueryEnhancement, setEnableQueryEnhancement] = useState(true);
  const [scoreThreshold, setScoreThreshold] = useState(0.7);
  const [topK, setTopK] = useState(10);
  const [denseWeight, setDenseWeight] = useState(0.9);
  const [selectedFolderIds, setSelectedFolderIds] = useState<string[]>([]);
  const [selectedToolIds, setSelectedToolIds] = useState<string[]>([]);
  const [selectedSkillIds, setSelectedSkillIds] = useState<string[]>([]);
  const [selectedSubagentIds, setSelectedSubagentIds] = useState<string[]>([]);
  const [enableSummary, setEnableSummary] = useState(true);
  const [lastNConversations, setLastNConversations] = useState(5);
  const [greeting, setGreeting] = useState('');
  const [allowedDomainsInput, setAllowedDomainsInput] = useState('');
  const [enableSmartSuggestions, setEnableSmartSuggestions] = useState(true);
  const [enablePresetQuestions, setEnablePresetQuestions] = useState(true);
  const [presetQuestions, setPresetQuestions] = useState<string[]>([]);
  const [guardrailOutputStrategy, setGuardrailOutputStrategy] =
    useState<GuardrailOutputStrategy>('block');
  const [configuredGuardrails, setConfiguredGuardrails] = useState<Record<string, GuardrailConfig>>(
    {}
  );
  const [guardrailErrors, setGuardrailErrors] = useState<Record<string, string>>({});
  // Previous safety mechanism state preserved:
  const [enablePreValidation, setEnablePreValidation] = useState(true);
  const [enablePostValidation, setEnablePostValidation] = useState(true);
  const [enablePiiDetection, setEnablePiiDetection] = useState(false);
  const [enableInjectionDetection, setEnableInjectionDetection] = useState(false);
  const [inputRiskThreshold, setInputRiskThreshold] = useState(0.7);
  const [outputRiskThreshold, setOutputRiskThreshold] = useState(0.8);
  const [enableKeywordCheck, setEnableKeywordCheck] = useState(false);
  const [bannedKeywords, setBannedKeywords] = useState<string[]>([]);
  const [bannedPatterns, setBannedPatterns] = useState<string[]>([]);
  const [enableCustomRules, setEnableCustomRules] = useState(false);
  const [customRules, setCustomRules] = useState<CustomRule[]>([]);
  const isHydratingRef = useRef(true);
  const hydratedForAgentIdRef = useRef<string | null>(null);
  const prevCapabilitiesRef = useRef<AgentCapabilitiesState | null>(null);
  const agentRef = useRef<Agent | undefined>(undefined);
  const lastSavedSignatureRef = useRef('');
  const currentSignatureRef = useRef('');
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const editPersonaSaveRef = useRef<(() => Promise<void>) | null>(null);
  const [isSavingPreview, setIsSavingPreview] = useState(false);
  const [isMobilePreviewOpen, setIsMobilePreviewOpen] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState<Date | null>(null);

  const {
    data,
    isLoading: isLoadingAgent,
    refetch: refetchAgent,
  } = useGetAgentById(
    { id: agentId, projectKey: blocksKey },
    { enabled: !!blocksKey && !!agentId, staleTime: 0, refetchOnMount: 'always' }
  );
  const { availableChatModels, availableCustomChatModels, isLoadingLlmModels } =
    useAgentChatModels();
  const { mutateAsync: updateAgentAIConfigurations } = useUpdateAgentAIConfigurations();
  const { mutateAsync: updateWidget } = useUpdateWidget();
  const { mutateAsync: publishAgent, isPending: isPublishing } = usePublishAgent();
  const [isPublishFlow, setIsPublishFlow] = useState(false);
  const agent = data?.agent;
  const userId = useCommonStore((state) => state.user?.id);
  const isMyOrganization =
    Boolean(agent) && normalizeResourceVisibility(agent?.visibility) === RESOURCE_VISIBILITY.PUBLIC;
  const isOwner = isAgentOwnerAccess(agent, userId);
  const canEdit = canEditAgentConfig(agent, userId);
  // The widget is saved under the server's own rule (creator in the agent's own org,
  // org admin or an explicit editor for one shared in), not under agent ownership.
  const canConfigureWidget = canConfigureAgentWidget(agent, userId);
  const isSharedWithMe = hasAgentShareAccess(agent);
  agentRef.current = agent;
  const agentName = agent?.name || agentText(t, 'FINANCE_ASSISTANT', 'Finance Assistant');
  const isEmptyWidgetId = Boolean(agent) && !agent?.widget_id;
  const isPublishBusy = isPublishing || isPublishFlow;
  const previewMessage = greeting || agent?.widget?.greeting || defaultWelcomeMessage;
  const tabItems = getAgentDetailsTabs(agentId, t);
  const { data: agentsListData } = useGetAgents(
    { limit: 100, offset: 0, project_key: blocksKey },
    { enabled: !!blocksKey, scope: 'agent-details-switcher' }
  );
  const availableAgents = agentsListData?.agents ?? [];
  const currentLlmModel = selectedLlmModel || agent?.llm_config?.model_name || '';
  const currentLlmProvider = selectedLlmProvider || agent?.llm_config?.provider || '';
  const currentLlmModelId = selectedLlmProvider
    ? selectedLlmModelId
    : (agent?.llm_config?.model_id || '');
  const currentIsBlocksProvider = selectedLlmProvider
    ? isBlocksProvider
    : (agent?.llm_config?.is_blocks_provider ?? true);

  const widgetPayload = useMemo(
    (): Widget => ({
      ...(agent?.widget ?? {}),
      // A custom widget name set on the Integration tab wins; otherwise use the agent's name.
      name: agent?.widget?.name || agent?.name || '',
      widget_settings: agent?.widget?.widget_settings ?? [],
      logo_url: agent?.widget?.logo_url ?? '',
      logo_id: agent?.widget?.logo_id ?? null,
      fe_script: agent?.widget?.fe_script ?? null,
      show_agent_name: agent?.widget?.show_agent_name ?? true,
      site_url: agent?.widget?.site_url ?? null,
      brand_color: agent?.widget?.brand_color ?? null,
      allowed_domains: parseAllowedDomains(allowedDomainsInput),
      allow_visitor_tool_connect:
        agent?.widget?.allow_visitor_tool_connect ?? agent?.widget?.AllowVisitorToolConnect ?? true,
      greeting,
      predefined_questions: presetQuestions.map((question) => question.trim()).filter(Boolean),
      enable_predefined_questions: enablePresetQuestions,
    }),
    [
      agent?.name,
      agent?.widget,
      allowedDomainsInput,
      enablePresetQuestions,
      greeting,
      presetQuestions,
    ]
  );

  const buildConfigPayload = useCallback((): IUpdateAgentAIConfigurationPayload | null => {
    const agentSnapshot = agentRef.current;
    if (!agentSnapshot) return null;

    return buildAgentConfigPayload(
      agentSnapshot,
      blocksKey,
      {
        sectionState,
        llmModel: currentLlmModel,
        llmProvider: currentLlmProvider,
        llmModelId: currentLlmModelId,
        isBlocksProvider: currentIsBlocksProvider,
        responseType,
        responseFormat,
        basePrompt,
        temperature: temperatureValue,
        maxTokens: maxResponseValue,
        enableSummary,
        lastNConversations,
        enableSmartSuggestions,
        enableQueryEnhancement,
        scoreThreshold,
        topK,
        denseWeight,
        guardrailOutputStrategy,
        configuredGuardrails,
        folderIds: selectedFolderIds,
        toolIds: selectedToolIds,
        skillIds: selectedSkillIds,
        subagentIds: selectedSubagentIds,
      },
      userId
    );
  }, [
    userId,
    blocksKey,
    sectionState,
    responseType,
    responseFormat,
    currentLlmModel,
    currentLlmProvider,
    currentLlmModelId,
    currentIsBlocksProvider,
    basePrompt,
    temperatureValue,
    maxResponseValue,
    enableSummary,
    lastNConversations,
    enableSmartSuggestions,
    enableQueryEnhancement,
    scoreThreshold,
    topK,
    denseWeight,
    guardrailOutputStrategy,
    configuredGuardrails,
    selectedFolderIds,
    selectedToolIds,
    selectedSkillIds,
    selectedSubagentIds,
  ]);

  const configSignature = useMemo(
    () =>
      JSON.stringify({
        sectionState,
        currentLlmModel,
        currentLlmProvider,
        currentLlmModelId,
        currentIsBlocksProvider,
        responseType,
        responseFormat,
        basePrompt,
        temperatureValue,
        maxResponseValue,
        enableQueryEnhancement,
        scoreThreshold,
        topK,
        denseWeight,
        selectedFolderIds,
        selectedToolIds,
        selectedSkillIds,
        selectedSubagentIds,
        enableSummary,
        lastNConversations,
        greeting,
        enableSmartSuggestions,
        enablePresetQuestions,
        presetQuestions,
        guardrailOutputStrategy,
        configuredGuardrails,
        enablePreValidation,
        enablePostValidation,
        enablePiiDetection,
        enableInjectionDetection,
        inputRiskThreshold,
        outputRiskThreshold,
        enableKeywordCheck,
        bannedKeywords,
        bannedPatterns,
        enableCustomRules,
        customRules,
      }),
    [
      sectionState,
      currentLlmModel,
      currentLlmProvider,
      currentLlmModelId,
      currentIsBlocksProvider,
      responseType,
      responseFormat,
      basePrompt,
      temperatureValue,
      maxResponseValue,
      enableQueryEnhancement,
      scoreThreshold,
      topK,
      denseWeight,
      selectedFolderIds,
      selectedToolIds,
      selectedSkillIds,
      selectedSubagentIds,
      enableSummary,
      lastNConversations,
      greeting,
      enableSmartSuggestions,
      enablePresetQuestions,
      presetQuestions,
      guardrailOutputStrategy,
      configuredGuardrails,
      enablePreValidation,
      enablePostValidation,
      enablePiiDetection,
      enableInjectionDetection,
      inputRiskThreshold,
      outputRiskThreshold,
      enableKeywordCheck,
      bannedKeywords,
      bannedPatterns,
      enableCustomRules,
      customRules,
    ]
  );

  useEffect(() => {
    currentSignatureRef.current = configSignature;
  }, [configSignature]);

  const isMountedRef = useRef(false);
  const prevPathnameRef = useRef(pathname);

  // Refetch agent when transitioning to /configuration route (avoiding duplicate calls on initial mount)
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      prevPathnameRef.current = pathname;
      return;
    }

    const isConfigurationRoute = pathname.includes('/configuration');
    const hasPathnameChanged = prevPathnameRef.current !== pathname;
    prevPathnameRef.current = pathname;

    if (hasPathnameChanged && isConfigurationRoute && agentId && blocksKey) {
      refetchAgent();
    }
  }, [pathname, agentId, blocksKey, refetchAgent]);

  // Reset hydration flags on route changes
  useEffect(() => {
    isHydratingRef.current = true;
    hydratedForAgentIdRef.current = null;
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
  }, [pathname, agentId]);

  useEffect(() => {
    if (!agent || agent.id !== agentId) return;
    const pageAgentKey = `${agentId}_${pathname}`;
    if (hydratedForAgentIdRef.current === pageAgentKey) return;
    hydratedForAgentIdRef.current = pageAgentKey;

    const incomingFolderId =
      (typeof locationState === 'object' && locationState && 'addFolderId' in locationState
        ? (locationState as { addFolderId?: string }).addFolderId
        : undefined) || new URLSearchParams(search).get('addFolder');
    const incomingToolId =
      (typeof locationState === 'object' && locationState && 'addToolId' in locationState
        ? (locationState as { addToolId?: string }).addToolId
        : undefined) || new URLSearchParams(search).get('addTool');
    const incomingSkillId =
      (typeof locationState === 'object' && locationState && 'addSkillId' in locationState
        ? (locationState as { addSkillId?: string }).addSkillId
        : undefined) || new URLSearchParams(search).get('addSkill');
    const incomingSubagentId =
      (typeof locationState === 'object' && locationState && 'addSubagentId' in locationState
        ? (locationState as { addSubagentId?: string }).addSubagentId
        : undefined) || new URLSearchParams(search).get('addSubagent');
    const saved = getAgentConfigValues(agent);
    const nextFolderIds =
      incomingFolderId && !saved.folderIds.includes(incomingFolderId)
        ? [...saved.folderIds, incomingFolderId]
        : saved.folderIds;
    const nextToolIds =
      incomingToolId && !saved.toolIds.includes(incomingToolId)
        ? [...saved.toolIds, incomingToolId]
        : saved.toolIds;
    const nextSkillIds =
      incomingSkillId && !saved.skillIds.includes(incomingSkillId)
        ? [...saved.skillIds, incomingSkillId]
        : saved.skillIds;
    const nextSubagentIds =
      incomingSubagentId &&
      incomingSubagentId !== agentId &&
      !saved.subagentIds.includes(incomingSubagentId)
        ? [...saved.subagentIds, incomingSubagentId]
        : saved.subagentIds;

    // A resource added via the URL switches its section on.
    const nextSectionState = {
      ...saved.sectionState,
      ...(incomingFolderId ? { 'knowledge-base': true } : {}),
      ...(incomingToolId ? { tools: true } : {}),
      ...(incomingSkillId ? { skills: true } : {}),
      ...(incomingSubagentId ? { subagents: true } : {}),
    };
    setSectionState(nextSectionState);
    const initialCapabilities = capabilitiesFromSectionState(nextSectionState);
    prevCapabilitiesRef.current = initialCapabilities;

    if (saved.llmModel) {
      setSelectedLlmModel(saved.llmModel);
    }
    setSelectedLlmProvider(saved.llmProvider);
    setSelectedLlmModelId(saved.llmModelId);
    setIsBlocksProvider(saved.isBlocksProvider);
    setResponseType(saved.responseType);
    setResponseFormat(saved.responseFormat);
    // Uses the capabilities above, so a section switched on via the URL is included.
    const initialBasePrompt = resolveInitialBasePrompt(
      agent.llm_config?.base_prompt ?? '',
      initialCapabilities
    );
    setBasePrompt(initialBasePrompt);
    setTemperatureValue(saved.temperature);
    setMaxResponseValue(saved.maxTokens);

    setEnableQueryEnhancement(saved.enableQueryEnhancement);
    setScoreThreshold(saved.scoreThreshold);
    setTopK(saved.topK);
    setDenseWeight(saved.denseWeight);
    setSelectedFolderIds(nextFolderIds);
    setSelectedToolIds(nextToolIds);
    setSelectedSkillIds(nextSkillIds);
    setSelectedSubagentIds(nextSubagentIds);

    setEnableSummary(saved.enableSummary);
    setLastNConversations(saved.lastNConversations);
    const initialGreeting = formatGreeting(agent.widget?.greeting ?? '', agent.name);
    setGreeting(initialGreeting);
    setAllowedDomainsInput((agent.widget?.allowed_domains ?? []).filter(Boolean).join(', '));
    setEnableSmartSuggestions(agent.enable_questions_suggestions ?? true);
    setEnablePresetQuestions(agent.widget?.enable_predefined_questions ?? true);
    setPresetQuestions(agent.widget?.predefined_questions ?? []);

    setGuardrailOutputStrategy(saved.guardrailOutputStrategy);
    setConfiguredGuardrails(saved.configuredGuardrails);

    setEnablePreValidation(agent.guardrail?.enable_pre_validation ?? true);
    setEnablePostValidation(agent.guardrail?.enable_post_validation ?? true);
    setEnablePiiDetection(agent.guardrail?.enable_pii_detection ?? false);
    setEnableInjectionDetection(agent.guardrail?.enable_injection_detection ?? false);
    setInputRiskThreshold(agent.guardrail?.input_risk_threshold ?? 0.7);
    setOutputRiskThreshold(agent.guardrail?.output_risk_threshold ?? 0.8);
    setEnableKeywordCheck(agent.guardrail?.enable_keyword_check ?? false);
    setBannedKeywords(agent.guardrail?.banned_keywords ?? []);
    setBannedPatterns(agent.guardrail?.banned_patterns ?? []);
    setEnableCustomRules(agent.guardrail?.enable_custom_rules ?? false);
    setCustomRules(agent.guardrail?.custom_rules ?? []);

    // What the server has, so a resource added via the URL still counts as unsaved.
    const initialSignature = JSON.stringify({
      sectionState: saved.sectionState,
      currentLlmModel: saved.llmModel,
      currentLlmProvider: saved.llmProvider,
      currentLlmModelId: saved.llmModelId,
      currentIsBlocksProvider: saved.isBlocksProvider,
      responseType: saved.responseType,
      responseFormat: saved.responseFormat,
      basePrompt: initialBasePrompt,
      temperatureValue: saved.temperature,
      maxResponseValue: saved.maxTokens,
      enableQueryEnhancement: saved.enableQueryEnhancement,
      scoreThreshold: saved.scoreThreshold,
      topK: saved.topK,
      denseWeight: saved.denseWeight,
      selectedFolderIds: saved.folderIds,
      selectedToolIds: saved.toolIds,
      selectedSkillIds: saved.skillIds,
      selectedSubagentIds: saved.subagentIds,
      enableSummary: saved.enableSummary,
      lastNConversations: saved.lastNConversations,
      greeting: initialGreeting,
      enableSmartSuggestions: agent.enable_questions_suggestions ?? true,
      enablePresetQuestions: agent.widget?.enable_predefined_questions ?? true,
      presetQuestions: agent.widget?.predefined_questions ?? [],
      guardrailOutputStrategy: saved.guardrailOutputStrategy,
      configuredGuardrails: saved.configuredGuardrails,
      enablePreValidation: agent.guardrail?.enable_pre_validation ?? true,
      enablePostValidation: agent.guardrail?.enable_post_validation ?? true,
      enablePiiDetection: agent.guardrail?.enable_pii_detection ?? false,
      enableInjectionDetection: agent.guardrail?.enable_injection_detection ?? false,
      inputRiskThreshold: agent.guardrail?.input_risk_threshold ?? 0.7,
      outputRiskThreshold: agent.guardrail?.output_risk_threshold ?? 0.8,
      enableKeywordCheck: agent.guardrail?.enable_keyword_check ?? false,
      bannedKeywords: agent.guardrail?.banned_keywords ?? [],
      bannedPatterns: agent.guardrail?.banned_patterns ?? [],
      enableCustomRules: agent.guardrail?.enable_custom_rules ?? false,
      customRules: agent.guardrail?.custom_rules ?? [],
    });

    lastSavedSignatureRef.current = initialSignature;
    isHydratingRef.current = false;

    if (
      (incomingFolderId || incomingToolId || incomingSkillId || incomingSubagentId) &&
      (search.includes('addFolder') ||
        search.includes('addTool') ||
        search.includes('addSkill') ||
        search.includes('addSubagent') ||
        (typeof locationState === 'object' &&
          locationState &&
          ('addFolderId' in locationState ||
            'addToolId' in locationState ||
            'addSkillId' in locationState ||
            'addSubagentId' in locationState)))
    ) {
      navigate(pathname, { replace: true, state: {} });
    }
  }, [agent, agentId, pathname, search, locationState, navigate]);

  const restoreLastSavedConfiguration = useCallback(() => {
    try {
      const saved = JSON.parse(lastSavedSignatureRef.current) as {
        selectedToolIds?: string[];
        selectedSkillIds?: string[];
        selectedSubagentIds?: string[];
        sectionState?: Record<string, boolean>;
      };
      if (saved.selectedToolIds) {
        setSelectedToolIds(saved.selectedToolIds);
      }
      if (saved.selectedSkillIds) {
        setSelectedSkillIds(saved.selectedSkillIds);
      }
      if (Array.isArray(saved.selectedSubagentIds)) {
        setSelectedSubagentIds(saved.selectedSubagentIds);
      }
      if (saved.sectionState) {
        setSectionState(saved.sectionState);
      }
    } catch {
      setSelectedToolIds(agentRef.current ? getAgentToolIds(agentRef.current) : []);
      setSelectedSkillIds(agentRef.current?.skill_ids ?? []);
      setSelectedSubagentIds(agentRef.current?.subagent_ids ?? []);
    }
  }, []);

  useEffect(() => {
    if (!canEdit || !agentRef.current || !blocksKey || isHydratingRef.current) {
      return;
    }

    if (lastSavedSignatureRef.current === configSignature) {
      return;
    }

    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const signatureToSave = configSignature;

    saveTimeoutRef.current = setTimeout(async () => {
      const widgetUpdatePayload: IUpdateAgentWidgetPayload = {
        agent_id: agentId,
        project_key: blocksKey,
        regen_widget_id: false,
        widget: widgetPayload,
      };
      const configPayload = buildConfigPayload();

      if (!configPayload) {
        return;
      }

      try {
        setIsSavingPreview(true);
        // Widget / embed is owner-only; editors save via update-ai-configurations.
        if (canConfigureWidget) {
          await updateWidget(widgetUpdatePayload);
        }
        await updateAgentAIConfigurations(configPayload);
        lastSavedSignatureRef.current = signatureToSave;
        setLastSaveTime(new Date());
      } catch (error) {
        restoreLastSavedConfiguration();

        const description =
          error instanceof HttpError && typeof error.error.detail === 'string'
            ? error.error.detail
            : agentText(
                t,
                'AGENT_CONFIGURATION_COULD_NOT_BE_SAVED',
                'Your changes could not be saved. Please try again.'
              );

        toast({
          variant: 'destructive',
          title: agentText(t, 'SAVE_FAILED', 'Save failed'),
          description,
        });
      } finally {
        setIsSavingPreview(false);
      }
    }, 700);

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [
    agentId,
    blocksKey,
    canEdit,
    configSignature,
    isOwner,
    t,
    toast,
    restoreLastSavedConfiguration,
    updateAgentAIConfigurations,
    updateWidget,
    buildConfigPayload,
    widgetPayload,
  ]);

  const handleRegisterEditSave = useCallback((saveFn: (() => Promise<void>) | null) => {
    editPersonaSaveRef.current = saveFn;
  }, []);

  const autoBasePrompt = useMemo(
    () => computeDefaultBasePrompt(capabilitiesFromSectionState(sectionState)),
    [sectionState]
  );

  const handleSectionStateChange = useCallback(
    (key: string, checked: boolean) => {
      const next = { ...sectionState, [key]: checked };

      if (key === 'tools' || key === 'skills' || key === 'subagents' || key === 'knowledge-base') {
        const prevCapabilities =
          prevCapabilitiesRef.current ?? capabilitiesFromSectionState(sectionState);
        const nextCapabilities = capabilitiesFromSectionState(next);
        const resyncedPrompt = resolveSyncedBasePrompt({
          proposedBasePrompt: basePrompt,
          prevCapabilities,
          nextCapabilities,
        });
        prevCapabilitiesRef.current = nextCapabilities;

        if (resyncedPrompt !== basePrompt) {
          setBasePrompt(resyncedPrompt);
          toast({
            variant: 'info',
            title: agentText(t, 'BASE_PROMPT_UPDATED', BASE_PROMPT_SYNC_NOTICE.title),
            description: agentText(
              t,
              'BASE_PROMPT_UPDATED_DESCRIPTION',
              BASE_PROMPT_SYNC_NOTICE.description
            ),
          });
        }
      }

      setSectionState(next);
    },
    [sectionState, basePrompt, t, toast]
  );

  const handleSaveConfiguration = useCallback(async () => {
    if (!agentId || !blocksKey) {
      toast({
        title: agentText(t, 'SAVE_FAILED', 'Save failed'),
        description: agentText(t, 'MISSING_AGENT_OR_PROJECT_KEY', 'Missing agent or project key.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (editPersonaSaveRef.current) {
        await editPersonaSaveRef.current();
      }

      const configPayload = buildConfigPayload();
      if (!configPayload) {
        return;
      }

      setIsSavingPreview(true);
      if (canConfigureWidget) {
        await updateWidget({
          agent_id: agentId,
          project_key: blocksKey,
          regen_widget_id: false,
          widget: widgetPayload,
        });
      }
      await updateAgentAIConfigurations(configPayload);
      lastSavedSignatureRef.current = configSignature;
      setLastSaveTime(new Date());

      toast({
        variant: 'success',
        title: agentText(t, 'SAVED', 'Saved'),
        description: agentText(
          t,
          'AGENT_CONFIGURATION_SAVED',
          'Your changes were saved successfully.'
        ),
      });
    } catch (error) {
      restoreLastSavedConfiguration();

      const description =
        error instanceof HttpError && typeof error.error.detail === 'string'
          ? error.error.detail
          : agentText(
              t,
              'AGENT_CONFIGURATION_COULD_NOT_BE_SAVED',
              'Your changes could not be saved. Please try again.'
            );

      toast({
        variant: 'destructive',
        title: agentText(t, 'SAVE_FAILED', 'Save failed'),
        description,
      });
    } finally {
      setIsSavingPreview(false);
    }
  }, [
    agentId,
    blocksKey,
    buildConfigPayload,
    configSignature,
    isOwner,
    t,
    toast,
    restoreLastSavedConfiguration,
    updateAgentAIConfigurations,
    updateWidget,
    widgetPayload,
  ]);

  const handlePublish = useCallback(async () => {
    if (!isOwner) {
      await handleSaveConfiguration();
      return;
    }

    if (!agentId || !blocksKey) {
      toast({
        title: agentText(t, 'UNABLE_TO_PUBLISH', 'Unable to publish'),
        description: agentText(t, 'MISSING_AGENT_OR_PROJECT_KEY', 'Missing agent or project key.'),
        variant: 'destructive',
      });
      return;
    }

    try {
      setIsPublishFlow(true);
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }

      if (editPersonaSaveRef.current) {
        await editPersonaSaveRef.current();
      }

      const configPayload = buildConfigPayload();
      if (configPayload) {
        // Widget edits are autosaved; publish only touches the widget to generate a
        // missing widget ID (older agents created without one).
        if (agent && !agent.widget_id) {
          await updateWidget({
            agent_id: agentId,
            project_key: blocksKey,
            regen_widget_id: true,
            widget: buildPublishWidgetPayload(agent, widgetPayload),
          });
        }
        await updateAgentAIConfigurations(configPayload);
        lastSavedSignatureRef.current = configSignature;
      }

      const response = (await publishAgent({
        agent_id: agentId,
        project_key: blocksKey,
      })) as IPublishAgentResponse;

      if (response.is_success) {
        toast({
          variant: 'success',
          title: agentText(t, 'AGENT_PUBLISHED', 'Agent published'),
          description: agentText(
            t,
            'AGENT_PUBLISHED_SUCCESSFULLY',
            'The agent was published successfully.'
          ),
        });
        return;
      }

      toast({
        title: agentText(t, 'PUBLISH_FAILED', 'Publish failed'),
        description:
          typeof response.detail === 'string'
            ? response.detail
            : agentText(t, 'AGENT_COULD_NOT_BE_PUBLISHED', 'The agent could not be published.'),
        variant: 'destructive',
      });
    } catch (error) {
      let description = agentText(
        t,
        'AGENT_COULD_NOT_BE_PUBLISHED',
        'The agent could not be published.'
      );

      if (error instanceof HttpError && typeof error.error?.detail === 'string') {
        description = error.error.detail;
      } else if (error instanceof Error && error.message) {
        try {
          const parsed = JSON.parse(error.message) as { detail?: unknown };
          description =
            typeof parsed.detail === 'string' && parsed.detail.trim()
              ? parsed.detail
              : error.message;
        } catch {
          description = error.message;
        }
      }

      toast({
        title: agentText(t, 'PUBLISH_FAILED', 'Publish failed'),
        description,
        variant: 'destructive',
      });
    } finally {
      setIsPublishFlow(false);
    }
  }, [
    agent,
    agentId,
    blocksKey,
    buildConfigPayload,
    configSignature,
    handleSaveConfiguration,
    isOwner,
    publishAgent,
    t,
    toast,
    updateAgentAIConfigurations,
    updateWidget,
    widgetPayload,
  ]);

  const handleTabClick = useCallback(
    (href: string) => {
      navigate(href);
    },
    [navigate]
  );

  const handleCancel = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  const handleBack = useCallback(() => {
    navigate('/agents');
  }, [navigate]);

  const handleAgentSelect = useCallback(
    (nextAgentId: string) => {
      if (nextAgentId === agentId) return;
      navigate(`/agent-details/${nextAgentId}/edit`);
    },
    [agentId, navigate]
  );

  const activeTabHref = useMemo(() => {
    const matchedTab = tabItems.find((tab) => pathname === tab.href);
    return matchedTab?.href ?? tabItems[0]?.href ?? '';
  }, [pathname, tabItems]);

  return (
    <div className="flex h-full min-h-0 w-full flex-1 flex-col">
      <AgentDetailsTopBar
        agentName={agentName}
        agents={availableAgents}
        tabs={tabItems}
        activeTabHref={activeTabHref}
        isLoading={isLoadingAgent}
        isPublishing={isPublishBusy}
        onBack={handleBack}
        onAgentSelect={handleAgentSelect}
        onTabClick={handleTabClick}
        onCancel={handleCancel}
        onSaveAndPublish={handlePublish}
        createdBy={agent?.created_by}
        isOwner={isOwner}
        canEdit={canEdit}
        isSharedWithMe={isSharedWithMe}
        isEmptyWidgetId={isEmptyWidgetId}
        isMyOrganization={isMyOrganization}
        reservePreviewColumn={
          pathname.includes('/edit') || pathname.includes('/configuration')
        }
      />

      <div className="min-h-0 flex-1 overflow-auto">
        {pathname.includes('/edit') ? (
          <div className="pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6">
            <EditAgent
              agentId={agentId}
              projectKey={blocksKey}
              agent={agent}
              isLoading={isLoadingAgent}
              isSaving={isSavingPreview}
              lastSaveTime={lastSaveTime}
              basePrompt={basePrompt}
              onBasePromptChange={setBasePrompt}
              onRegisterSave={handleRegisterEditSave}
            />
          </div>
        ) : pathname.includes('/configuration') ? (
          <div className="pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6">
            <div
              data-testid="agent-config-workspace"
              className="flex w-full flex-col items-stretch gap-4 lg:flex-row lg:gap-5"
            >
              <div className="min-w-0 flex-1">
                <AgentConfigurationCard
                  headerAction={
                    <MobilePreviewToggle onClick={() => setIsMobilePreviewOpen(true)} />
                  }
                  agentId={agentId}
                  isAgentDetailsLoading={isLoadingAgent}
                  agent={agent}
                  createdBy={agent?.created_by}
                  sectionState={sectionState}
                  onSectionStateChange={handleSectionStateChange}
                  isLoadingLlmModels={isLoadingLlmModels}
                  availableChatModels={availableChatModels}
                  availableCustomChatModels={availableCustomChatModels}
                  currentLlmModel={currentLlmModel}
                  currentLlmProvider={currentLlmProvider}
                  currentLlmModelId={currentLlmModelId}
                  isBlocksProvider={currentIsBlocksProvider}
                  onModelChange={({
                    modelName,
                    provider,
                    isBlocksProvider,
                    modelId,
                    temperature,
                    maxTokens,
                  }) => {
                    setSelectedLlmModel(modelName);
                    setSelectedLlmProvider(provider);
                    setSelectedLlmModelId(modelId || '');
                    setIsBlocksProvider(isBlocksProvider);
                    setTemperatureValue(temperature);
                    setMaxResponseValue(maxTokens);
                  }}
                  responseType={responseType}
                  onResponseTypeChange={(value) => {
                    setResponseType(value);
                    setResponseFormat(
                      value === 'json' ? responseFormat || defaultJsonResponseFormat : null
                    );
                  }}
                  responseFormat={responseFormat}
                  onResponseFormatChange={setResponseFormat}
                  basePrompt={basePrompt}
                  onBasePromptChange={setBasePrompt}
                  autoBasePrompt={autoBasePrompt}
                  temperatureValue={temperatureValue}
                  onTemperatureChange={setTemperatureValue}
                  maxResponseValue={maxResponseValue}
                  onMaxResponseChange={setMaxResponseValue}
                  isModelParametersOpen={isModelParametersOpen}
                  onModelParametersOpenChange={setIsModelParametersOpen}
                  enableQueryEnhancement={enableQueryEnhancement}
                  onEnableQueryEnhancementChange={setEnableQueryEnhancement}
                  scoreThreshold={scoreThreshold}
                  onScoreThresholdChange={setScoreThreshold}
                  topK={topK}
                  onTopKChange={setTopK}
                  denseWeight={denseWeight}
                  onDenseWeightChange={setDenseWeight}
                  projectKey={blocksKey}
                  selectedFolderIds={selectedFolderIds}
                  onSelectedFolderIdsChange={setSelectedFolderIds}
                  selectedToolIds={selectedToolIds}
                  onSelectedToolIdsChange={setSelectedToolIds}
                  selectedSkillIds={selectedSkillIds}
                  onSelectedSkillIdsChange={setSelectedSkillIds}
                  selectedSubagentIds={selectedSubagentIds}
                  onSelectedSubagentIdsChange={setSelectedSubagentIds}
                  enableSummary={enableSummary}
                  onEnableSummaryChange={setEnableSummary}
                  lastNConversations={lastNConversations}
                  onLastNConversationsChange={setLastNConversations}
                  agentName={agentName}
                  greeting={greeting}
                  onGreetingChange={setGreeting}
                  enableSmartSuggestions={enableSmartSuggestions}
                  onEnableSmartSuggestionsChange={setEnableSmartSuggestions}
                  enablePresetQuestions={enablePresetQuestions}
                  onEnablePresetQuestionsChange={setEnablePresetQuestions}
                  presetQuestions={presetQuestions}
                  onPresetQuestionsChange={setPresetQuestions}
                  guardrailOutputStrategy={guardrailOutputStrategy}
                  onGuardrailOutputStrategyChange={setGuardrailOutputStrategy}
                  configuredGuardrails={configuredGuardrails}
                  onConfiguredGuardrailsChange={setConfiguredGuardrails}
                  guardrailErrors={guardrailErrors}
                  enablePreValidation={enablePreValidation}
                  onEnablePreValidationChange={setEnablePreValidation}
                  enablePostValidation={enablePostValidation}
                  onEnablePostValidationChange={setEnablePostValidation}
                  enablePiiDetection={enablePiiDetection}
                  onEnablePiiDetectionChange={setEnablePiiDetection}
                  enableInjectionDetection={enableInjectionDetection}
                  onEnableInjectionDetectionChange={setEnableInjectionDetection}
                  inputRiskThreshold={inputRiskThreshold}
                  onInputRiskThresholdChange={setInputRiskThreshold}
                  outputRiskThreshold={outputRiskThreshold}
                  onOutputRiskThresholdChange={setOutputRiskThreshold}
                  enableKeywordCheck={enableKeywordCheck}
                  onEnableKeywordCheckChange={setEnableKeywordCheck}
                  bannedKeywords={bannedKeywords}
                  onBannedKeywordsChange={setBannedKeywords}
                  bannedPatterns={bannedPatterns}
                  onBannedPatternsChange={setBannedPatterns}
                  enableCustomRules={enableCustomRules}
                  onEnableCustomRulesChange={setEnableCustomRules}
                  customRules={customRules}
                  onCustomRulesChange={setCustomRules}
                />
              </div>
              <MobilePreviewPanel
                open={isMobilePreviewOpen}
                onClose={() => setIsMobilePreviewOpen(false)}
                className="min-w-0"
              >
                <div className="flex h-full min-h-0 w-full min-w-0 flex-col lg:absolute lg:inset-0 lg:h-auto">
                  <AgentsConfigPreviewSection
                    agent={agent}
                    projectKey={blocksKey}
                    isSaving={isSavingPreview}
                    lastSaveTime={lastSaveTime}
                  />
                </div>
              </MobilePreviewPanel>
            </div>
          </div>
        ) : pathname.includes('/integrations') ? (
          <div className="pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6">
            <AgentIntegrationHub
              id={agentId}
              allowedDomainsInput={allowedDomainsInput}
              onAllowedDomainsInputChange={setAllowedDomainsInput}
            />
          </div>
        ) : pathname.includes('/conversations') ? (
          <div className="pb-4 pt-4 sm:px-6 sm:pb-6 sm:pt-6 md:flex md:h-full md:min-h-0 md:flex-col">
            <AgentConversation agentId={agentId} />
          </div>
        ) : (
          <div className="flex flex-col gap-4 pt-4 sm:px-6 sm:pt-6">
            <div className="min-w-0 flex-1 space-y-6">
              {pathname.includes('/retrieval-testing') && (
                <div className="space-y-6">
                  <div className="mb-6">
                    <h3 className="text-2xl font-bold tracking-tight">
                      {agentText(t, 'RETRIEVAL_TESTING', 'Retrieval Testing')}
                    </h3>
                  </div>
                  <AgentsRetrievalTesting agentId={agentId} />
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
